import React, { useEffect } from 'react';

interface GoogleAdsConversionProps {
  // Google Ads Conversion ID, e.g. "AW-17422177310".
  id: string;
}

// Detect react-snap's puppeteer UA so the tag isn't injected into the
// prerendered HTML (it would otherwise double-fire when the page hydrates).
const IS_PRERENDERING =
  typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

const GTAG_SRC_PREFIX = 'https://www.googletagmanager.com/gtag/js';

/**
 * Loads the Google tag (gtag.js) for the Google Ads account and configures it,
 * so Google Ads conversions can be fired from code. Mirrors GoogleTagManager /
 * MetaPixel: the `gtag` stub + config are queued synchronously on mount and the
 * script download is deferred off the critical path (first of:
 * requestIdleCallback, the first user interaction, or a 3s timeout).
 *
 * The site already loads GTM (for GA4); this adds the standalone Google Ads
 * (AW-) tag only — they coexist on the shared dataLayer. The actual conversion
 * is fired via window.gtag('event','conversion', ...) from trackGenerateLead()
 * in src/lib/utils/dataLayer.ts, so every lead path is covered from one place.
 */
export const GoogleAdsConversion: React.FC<GoogleAdsConversionProps> = ({
  id,
}) => {
  useEffect(() => {
    // Don't execute if the ID is empty/invalid, or during prerender.
    if (!id || id.trim() === '' || IS_PRERENDERING) {
      return;
    }

    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const w = window as unknown as {
      dataLayer?: unknown[];
      gtag?: (..._args: unknown[]) => void;
      requestIdleCallback?: (
        // eslint-disable-next-line no-unused-vars
        _cb: () => void,
        // eslint-disable-next-line no-unused-vars
        _opts?: { timeout: number }
      ) => number;
    };

    // Set up the gtag stub + config immediately so commands fired before
    // gtag.js loads are queued and replayed. gtag must push the native
    // `arguments` object (not a rest array) for gtag.js to parse it as a command.
    w.dataLayer = w.dataLayer || [];
    if (!w.gtag) {
      w.gtag = function () {
        // eslint-disable-next-line prefer-rest-params
        (w.dataLayer as unknown[]).push(arguments);
      } as (..._args: unknown[]) => void;
    }
    w.gtag('js', new Date());
    w.gtag('config', id);

    let injected = false;
    const interactionEvents = [
      'pointerdown',
      'keydown',
      'touchstart',
      'scroll',
    ] as const;

    const cleanupListeners = () => {
      interactionEvents.forEach(evt => {
        window.removeEventListener(evt, inject);
      });
    };

    function inject() {
      if (injected) return;
      injected = true;
      cleanupListeners();

      try {
        // Guard against double-injection.
        if (document.querySelector(`script[src^="${GTAG_SRC_PREFIX}"]`)) {
          return;
        }

        const script = document.createElement('script');
        script.async = true;
        script.src = `${GTAG_SRC_PREFIX}?id=${id}`;
        script.onerror = () => {
          console.warn('Google Ads gtag script failed to load');
        };
        document.head.appendChild(script);
      } catch (e) {
        console.warn('Google Ads tag initialization failed:', e);
      }
    }

    // Defer the script download: idle callback (with timeout), first user
    // interaction, or a hard 3s fallback — whichever fires first.
    let idleHandle: number | undefined;
    if (typeof w.requestIdleCallback === 'function') {
      idleHandle = w.requestIdleCallback(inject, { timeout: 3000 });
    }

    interactionEvents.forEach(evt => {
      window.addEventListener(evt, inject, { once: true, passive: true });
    });

    const timeoutId = window.setTimeout(inject, 3000);

    return () => {
      cleanupListeners();
      window.clearTimeout(timeoutId);
      if (
        idleHandle !== undefined &&
        typeof (
          window as unknown as { cancelIdleCallback?: (_h: number) => void }
        ).cancelIdleCallback === 'function'
      ) {
        (
          window as unknown as { cancelIdleCallback: (_h: number) => void }
        ).cancelIdleCallback(idleHandle);
      }
    };
  }, [id]);

  return null;
};

export default GoogleAdsConversion;
